// so yea i realised that the button component still uses scss so
// yea, have fun with that

import { cva } from "class-variance-authority";

const grayButtonTone = "bg-white! text-gray-800! border border-gray-300 hover:bg-gray-50! hover:text-gray-800! active:bg-gray-100! active:text-gray-800! dark:bg-dark-800! dark:text-gray-200! dark:border-gray-600 dark:hover:bg-dark-700! dark:hover:text-gray-200! dark:active:bg-dark-600!";

export const buttonContainerCva = cva("", {
  variants: {
    loading: {
      true: "cursor-wait",
      false: "",
    },
    disabled: {
      true: "cursor-not-allowed",
      false: "",
    },
    iconOnly: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    loading: false,
    disabled: false,
    iconOnly: false,
  },
});

export const buttonCva = cva(
  "inline-flex flex-row items-center justify-center cursor-pointer transition-[color,background-color,border-color,transform] duration-100 ease-in select-none rounded outline-hidden border-solid tracking-wider min-w-0 max-w-full whitespace-nowrap overflow-hidden text-ellipsis relative",
  {
    variants: {
      variant: {
        primary: "",
        secondary: "",
        success: "",
        info: "",
        warning: "",
        danger: "",
        light: "bg-theme-light-input-background text-primary-600! dark:bg-theme-dark-input-background dark:text-primary-500! border border-theme-light-input-background-active dark:border-theme-dark-input-background-active hover:bg-theme-light-input-background-hover dark:hover:bg-theme-dark-input-background-hover active:bg-theme-light-input-background-active dark:active:bg-theme-dark-input-background-active",
        gray: grayButtonTone,
        custom: "bg-theme-light-input-background dark:bg-theme-dark-input-background border border-theme-light-input-background-active dark:border-theme-dark-input-background-active hover:bg-theme-light-input-background-hover dark:hover:bg-theme-dark-input-background-hover active:bg-theme-light-input-background-active dark:active:bg-theme-dark-input-background-active",
        white: "dark:bg-dark-800 border border-theme-light-input-background-active dark:border-theme-dark-input-background-active hover:bg-theme-light-input-background-hover dark:hover:bg-theme-dark-input-background-hover active:bg-theme-light-input-background-active dark:active:bg-theme-dark-input-background-active",
        // Matches VInput chrome (`--input-border` / muted icon text) for quiet toolbar icon buttons
        input: "bg-(--input-background) border border-(--input-border) text-gray-800! dark:text-gray-200! hover:bg-(--input-background-hover) active:bg-(--input-background-active)",
      },
      size: {
        small: "h-7 text-xs",
        medium: "h-8 text-sm",
        large: "h-10 text-lg",
        // The 44px touch target of a full-width action on a phone page (the submit bar of a guest form), 14px text.
        touch: "h-11 text-sm",
      },
      // Text colours live in the variant or its compounds, never here: an unconditional `text-white` competed with
      // the white variant's primary text and left its label invisible on the white fill.
      outlined: {
        true: "",
        false: "",
      },
      rounded: {
        true: "rounded-full",
        false: "rounded",
      },
      loading: {
        true: "justify-center",
        false: "",
      },
      hasIconLeft: {
        true: "",
        false: "",
      },
      hasIconRight: {
        true: "",
        false: "",
      },
      // Keyboard focus is an outline, not a ring: its 2px offset gap stays transparent, so it separates from
      // solid fills on any surface without a ring-offset colour. `outline-solid` is required because the base
      // `outline-hidden` sets `--tw-outline-style: none`, which `outline-2` would otherwise inherit.
      disabled: {
        true: "pointer-events-none opacity-50",
        false: "focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2",
      },
      static: {
        true: "",
        false: "",
      },
      // An icon-only button is a circle (the house style for round icon buttons): as wide as it is high and
      // fully rounded, and it never scales: the icon stays put on hover, focus and press. VButton passes `rounded` along, so the `rounded` class never competes with `rounded-full`.
      iconOnly: {
        true: "aspect-square",
        false: "",
      },
    },
    compoundVariants: [
      // Small size padding variants
      {
        size: "small",
        hasIconLeft: false,
        iconOnly: false,
        class: "px-2.5",
      },
      {
        size: "small",
        hasIconLeft: true,
        iconOnly: false,
        class: "px-2.5",
      },
      // Medium size padding variants
      {
        size: "medium",
        hasIconLeft: false,
        iconOnly: false,
        class: "px-5",
      },
      {
        size: "medium",
        hasIconLeft: true,
        iconOnly: false,
        class: "px-4",
      },
      // Large size padding variants
      {
        size: "large",
        hasIconLeft: false,
        iconOnly: false,
        class: "px-6",
      },
      {
        size: "large",
        hasIconLeft: true,
        iconOnly: false,
        class: "px-5",
      },
      // Touch size padding
      {
        size: "touch",
        iconOnly: false,
        class: "px-5",
      },
      // Press feedback: only a button with text shrinks. A static or icon-only button never scales, so the icon stays put.
      { iconOnly: false, static: false, class: "active:scale-[0.96]" },
      { size: "small", iconOnly: true, class: "w-7 px-0" },
      { size: "medium", iconOnly: true, class: "w-8 px-0" },
      { size: "large", iconOnly: true, class: "w-10 px-0" },
      { size: "touch", iconOnly: true, class: "w-11 px-0" },
      // Solid fills live apart from the outlined styles: without a merge step the CSS order would decide
      // between `bg-transparent` and the fill, and `bg-warning-600` / `bg-white` sort after it.
      { variant: "primary", outlined: false, class: "text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800" },
      { variant: "secondary", outlined: false, class: "text-white bg-secondary-600 hover:bg-secondary-700 active:bg-secondary-800" },
      { variant: "success", outlined: false, class: "text-white bg-success-600 hover:bg-success-700 active:bg-success-800" },
      { variant: "info", outlined: false, class: "text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800" },
      { variant: "warning", outlined: false, class: "text-white bg-warning-600 hover:bg-warning-700 active:bg-warning-800" },
      { variant: "danger", outlined: false, class: "text-white bg-danger-600 hover:bg-danger-700 active:bg-danger-800" },
      { variant: "custom", outlined: false, class: "text-white" },
      { variant: "white", outlined: false, class: "bg-white text-primary-500" },
      // Outlined variants
      {
        variant: "primary",
        outlined: true,
        class: "bg-transparent border border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white active:bg-primary-700 active:text-white",
      },
      {
        variant: "secondary",
        outlined: true,
        class: "bg-transparent border border-secondary-600 text-secondary-600 hover:bg-secondary-600 hover:text-white active:bg-secondary-700 active:text-white",
      },
      {
        variant: "success",
        outlined: true,
        class: "bg-transparent border border-success-600 text-success-600 hover:bg-success-600 hover:text-white active:bg-success-700 active:text-white",
      },
      {
        variant: "info",
        outlined: true,
        class: "bg-transparent border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white active:bg-blue-700 active:text-white",
      },
      {
        variant: "warning",
        outlined: true,
        class: "bg-transparent border border-warning-600 text-warning-600 hover:bg-warning-600 hover:text-white active:bg-warning-700 active:text-white",
      },
      {
        variant: "danger",
        outlined: true,
        class: "bg-transparent border border-danger-600 text-danger-600 hover:bg-danger-600 hover:text-white active:bg-danger-700 active:text-white",
      },
      {
        variant: "light",
        outlined: true,
        class: "bg-transparent border border-theme-light-input-background dark:border-theme-dark-input-background text-theme-light-input-background dark:text-theme-dark-input-background hover:bg-theme-light-input-background dark:hover:bg-theme-dark-input-background hover:text-primary-500 active:bg-theme-light-input-background-active dark:active:bg-theme-dark-input-background-active",
      },
      {
        variant: "gray",
        outlined: true,
        class: grayButtonTone,
      },
      {
        variant: "custom",
        outlined: true,
        class: "bg-transparent border border-theme-light-input-background dark:border-theme-dark-input-background text-theme-light-input-background dark:text-theme-dark-input-background hover:bg-theme-light-input-background dark:hover:bg-theme-dark-input-background hover:text-inherit active:bg-theme-light-input-background-active dark:active:bg-theme-dark-input-background-active",
      },
      {
        variant: "white",
        outlined: true,
        class: "bg-transparent border border-white text-white hover:bg-white hover:text-primary-600 active:bg-white active:text-primary-500",
      },
      {
        variant: "input",
        outlined: true,
        class: "bg-transparent border border-(--input-border) text-gray-800! dark:text-gray-200! hover:bg-(--input-background-hover) active:bg-(--input-background-active)",
      },
      // Focus colour: exactly one per combination, since these classes are not merged.
      {
        variant: ["primary", "secondary", "success", "info", "warning", "danger", "light", "gray", "custom", "input"],
        disabled: false,
        class: "focus-visible:outline-primary-500",
      },
      { variant: "white", outlined: false, disabled: false, class: "focus-visible:outline-primary-500" },
      // The outlined white button sits on primary-filled surfaces (bulk actions toolbar), where a primary outline disappears.
      { variant: "white", outlined: true, disabled: false, class: "focus-visible:outline-white" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "medium",
      outlined: false,
      rounded: true,
      loading: false,
      hasIconLeft: false,
      hasIconRight: false,
      disabled: false,
      static: false,
      iconOnly: false,
    },
  },
);

export const buttonIconCva = cva("shrink-0", {
  variants: {
    position: {
      left: "mr-2",
      right: "ml-2.5",
    },
    size: {
      small: "text-xs",
      medium: "text-sm",
      large: "text-base",
      touch: "text-base",
    },
    iconOnly: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      position: "left",
      iconOnly: true,
      class: "mr-0!",
    },
    {
      position: "right",
      iconOnly: true,
      class: "ml-0!",
    },
  ],
  defaultVariants: {
    position: "left",
    size: "medium",
    iconOnly: false,
  },
});

export const buttonLoaderCva = cva("", {
  variants: {
    size: {
      small: "text-xs",
      medium: "text-sm",
      large: "text-base",
      touch: "text-base",
    },
  },
  defaultVariants: {
    size: "medium",
  },
});
